import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Crown, MapPin, Radio, Trophy, Flame } from "lucide-react";
import { LogoLockup } from "@/components/Logo";
import Avatar from "@/components/Avatar";
import { BadgeArt, BADGE_META } from "@/components/BadgeArt";
import { API_URL } from "@/lib/api";

const RARITY_LABEL = { bronze: "text-[#e8b98a]", silver: "text-[#c9d3e4]", gold: "text-amber", legendary: "text-[#ff9df2]" };

function StatChip({ icon: Icon, value, label }) {
  return (
    <div className="rounded-2xl border border-ice/15 bg-navy-800/60 px-4 py-3 text-center">
      <Icon size={15} className="mx-auto text-magenta" />
      <div className="mt-1 font-mono text-lg font-bold text-cream">{value}</div>
      <div className="font-mono text-[9px] uppercase tracking-[0.2em] text-mut">{label}</div>
    </div>
  );
}

function ProfileModal({ pid, onClose }) {
  const [p, setP] = useState(null);
  const [missing, setMissing] = useState(false);
  useEffect(() => {
    fetch(`${API_URL}/players/${pid}/public`)
      .then(async (r) => { if (!r.ok) throw new Error(); setP((await r.json()).player); })
      .catch(() => setMissing(true));
  }, [pid]);
  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 flex items-center justify-center bg-navy-900/90 p-5 backdrop-blur-md" onClick={onClose}>
      <motion.div initial={{ scale: 0.9, y: 20 }} animate={{ scale: 1, y: 0 }}
        className="max-h-[88vh] w-full max-w-lg overflow-y-auto rounded-3xl border-2 border-amber/40 bg-navy-800 p-7"
        onClick={(e) => e.stopPropagation()} data-testid="champions-profile">
        {missing ? (
          <p className="py-6 text-center text-mut">This player keeps a private profile.</p>
        ) : !p ? (
          <p className="py-6 text-center text-mut">Opening the hall of fame…</p>
        ) : (
          <>
            <div className="flex items-center gap-4">
              <Avatar icon={p.avatar_icon} color={p.avatar_color} config={p.avatar_config} size={72} />
              <div className="min-w-0">
                <h2 className="truncate font-display text-2xl font-extrabold text-cream" data-testid="champions-profile-name">{p.name}</h2>
                <p className="font-mono text-xs uppercase tracking-[0.2em] text-mut">
                  {p.rank ? `#${p.rank} all-time` : "Unranked"} · {p.total_points} pts
                </p>
              </div>
            </div>
            <div className="mt-5 grid grid-cols-4 gap-2">
              <StatChip icon={Radio} value={p.games_played} label="games" />
              <StatChip icon={Trophy} value={p.wins} label="wins" />
              <StatChip icon={Flame} value={p.best_streak} label="best streak" />
              <StatChip icon={MapPin} value={p.venues_visited} label="venues" />
            </div>
            <div className="mt-6">
              <div className="mb-3 font-mono text-[10px] font-bold uppercase tracking-[0.3em] text-amber">
                Badges ({p.badges.length})
              </div>
              {p.badges.length ? (
                <div className="grid grid-cols-4 gap-3 sm:grid-cols-5">
                  {p.badges.map((bid) => {
                    const meta = BADGE_META[bid] || { name: bid, rarity: "bronze" };
                    return (
                      <div key={bid} className="flex flex-col items-center gap-1 text-center" data-testid={`champions-badge-${bid}`}>
                        <BadgeArt id={bid} size={46} />
                        <span className="text-[9px] font-bold leading-tight text-mut">{meta.name}</span>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <p className="text-sm text-mut">No badges yet — the wall is waiting.</p>
              )}
            </div>
          </>
        )}
      </motion.div>
    </motion.div>
  );
}

export default function Champions() {
  const [data, setData] = useState(null);
  const [pid, setPid] = useState(null);
  useEffect(() => {
    fetch(`${API_URL}/champions`)
      .then(async (r) => { if (r.ok) setData(await r.json()); })
      .catch(() => setData({ players: [], enabled: true }));
  }, []);
  const players = (data && data.players) || [];
  const podium = players.slice(0, 3);
  const rest = players.slice(3);
  return (
    <div className="min-h-screen bg-navy-900 px-5 py-8" data-testid="champions-page">
      <div className="mx-auto max-w-5xl">
        <div className="mb-8 flex items-center justify-between">
          <div>
            <div className="font-mono text-[10px] font-bold uppercase tracking-[0.4em] text-magenta">Braintap</div>
            <h1 className="font-display text-4xl font-extrabold uppercase tracking-tight text-cream">Champions Wall</h1>
            <p className="mt-1 text-sm text-mut">The hall of fame — every badge on this wall was earned, live, at a real table.</p>
          </div>
          <Link data-testid="champions-back" to="/" className="flex items-center gap-2 rounded-full border-2 border-ice/25 px-5 py-2.5 text-sm font-bold text-cream transition-all hover:border-amber hover:text-amber">
            <ArrowLeft size={16} /> Site
          </Link>
        </div>

        {!!(data && data.nightly && data.nightly.length) && (
          <div className="mb-8 rounded-3xl border border-amber/30 bg-navy-800/50 p-5" data-testid="champions-nightly">
            <div className="mb-3 flex items-center gap-2 font-mono text-[10px] font-bold uppercase tracking-[0.3em] text-amber">
              <Trophy size={13} /> Last night's champions
            </div>
            <div className="flex flex-wrap gap-2">
              {data.nightly.map((n, i) => (
                <div key={i} className="flex items-center gap-2 rounded-full border border-ice/15 bg-navy-900/70 px-4 py-2" data-testid="champions-nightly-chip">
                  <span className="text-xs text-mut">{n.venue_name}</span>
                  <span className="text-sm font-extrabold text-cream">{(n.champion || {}).name || "—"}</span>
                  <span className="font-mono text-xs font-bold text-mint">{(n.champion || {}).total ?? 0} pts</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {data && data.enabled === false ? (
          <p className="rounded-3xl border border-ice/15 bg-navy-800/60 p-8 text-center text-mut" data-testid="champions-disabled">
            Public player profiles are turned off right now.
          </p>
        ) : !data ? (
          <p className="py-10 text-center text-mut">Polishing the trophies…</p>
        ) : players.length === 0 ? (
          <p className="rounded-3xl border border-ice/15 bg-navy-800/60 p-8 text-center text-mut" data-testid="champions-empty">
            No champions yet — the first games of the night write history. Play at a venue and claim this wall.
          </p>
        ) : (
          <>
            <div className="mb-8 grid grid-cols-3 items-end gap-3" data-testid="champions-podium">
              {[podium[1], podium[0], podium[2]].filter(Boolean).map((p, i) => {
                const place = i === 1 ? 1 : i === 0 ? 2 : 3;
                const h = place === 1 ? "pb-10" : place === 2 ? "pb-4" : "pb-6";
                return (
                  <motion.button key={p.id} initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: i * 0.12 }} onClick={() => setPid(p.id)}
                    className={`rounded-3xl border-2 p-5 text-center transition-transform hover:-translate-y-1 ${place === 1 ? "border-amber bg-amber/10 shadow-[0_0_50px_rgba(255,201,60,0.25)]" : "border-ice/20 bg-navy-800/70"} ${h}`}
                    data-testid={`champions-podium-${place}`}>
                    <Crown size={place === 1 ? 26 : 18} className={`mx-auto ${place === 1 ? "text-amber" : "text-mut"}`} />
                    <div className="mt-2 flex justify-center"><Avatar icon={p.avatar_icon} color={p.avatar_color} config={p.avatar_config} size={place === 1 ? 76 : 58} /></div>
                    <div className="mt-2 truncate font-display text-base font-extrabold text-cream">{p.name}</div>
                    <div className="font-mono text-xs font-bold text-amber-soft">{p.total_points} pts</div>
                    {p.badges.length > 0 && (
                      <div className="mt-2 flex justify-center"><BadgeArt id={p.badges[0]} size={30} /></div>
                    )}
                  </motion.button>
                );
              })}
            </div>

            {rest.length > 0 && (
              <div className="overflow-hidden rounded-3xl border border-ice/15" data-testid="champions-grid">
                {rest.map((p, i) => (
                  <button key={p.id} onClick={() => setPid(p.id)}
                    className="flex w-full items-center gap-4 border-b border-ice/10 bg-navy-800/50 px-5 py-3 text-left transition-colors last:border-0 hover:bg-navy-800"
                    data-testid={`champions-row-${i}`}>
                    <span className="font-mono w-7 text-right text-sm font-bold text-mut">{i + 4}</span>
                    <Avatar icon={p.avatar_icon} color={p.avatar_color} config={p.avatar_config} size={38} />
                    <span className="min-w-0 flex-1 truncate font-display text-base font-bold text-cream">{p.name}</span>
                    <span className="hidden items-center gap-1 sm:flex">
                      {p.badges.slice(0, 4).map((bid) => <BadgeArt key={bid} id={bid} size={26} />)}
                    </span>
                    <span className="font-mono w-16 text-right font-bold text-mint">{p.total_points}</span>
                  </button>
                ))}
              </div>
            )}
          </>
        )}

        <footer className="mt-10 text-center font-mono text-[10px] uppercase tracking-[0.3em] text-mut/60">
          <Link to="/" className="text-mut transition-colors hover:text-amber">braintap.bar</Link> · live trivia for bars and couches
        </footer>
      </div>
      {pid && <ProfileModal pid={pid} onClose={() => setPid(null)} />}
    </div>
  );
}
